import {
  PROJECT_ICON_COLORS,
  PROJECT_ICONS,
  type ProjectIcon as ProjectIconName,
  type ProjectIconColor,
} from '@tms/shared';
import { Check } from 'lucide-react';
import { useEnumLabels, useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { ProjectIcon } from './ProjectIcon';

/** The swatch of each colour, as the label picker draws its dots. */
const SWATCH_STYLES: Record<ProjectIconColor, string> = {
  gray: 'bg-ink-subtle',
  blue: 'bg-accent-fg',
  green: 'bg-pass',
  amber: 'bg-block',
  red: 'bg-fail',
  purple: 'bg-accent',
};

export interface ProjectIconPickerProps {
  icon: ProjectIconName;
  color: ProjectIconColor;
  onChange: (next: { icon: ProjectIconName; color: ProjectIconColor }) => void;
  /** Ties the group to its field label. */
  id?: string;
}

/**
 * Picks the icon of a project and its tint.
 *
 * A grid of the whole set rather than a dropdown: there are twenty of them,
 * they are the point of the field, and choosing one is a glance rather than a
 * read. The preview is the same component the project list draws, so what is
 * picked is exactly what will be seen.
 */
export const ProjectIconPicker = ({ icon, color, onChange, id }: ProjectIconPickerProps) => {
  const { t } = useT();
  const { labelColor, projectIcon } = useEnumLabels();

  return (
    <div id={id} className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <ProjectIcon icon={icon} color={color} size="md" />
        {/* The tints, beside the preview they apply to. */}
        <div role="radiogroup" aria-label={t('projects.iconColor')} className="flex gap-1.5">
          {PROJECT_ICON_COLORS.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={value === color}
              aria-label={labelColor(value)}
              onClick={() => onChange({ icon, color: value })}
              className={cn(
                'flex size-6 items-center justify-center rounded-full border transition-colors',
                value === color ? 'border-ink-subtle' : 'border-transparent hover:border-border',
              )}
            >
              <span className={cn('size-3.5 rounded-full', SWATCH_STYLES[value])} />
            </button>
          ))}
        </div>
      </div>

      <div
        role="radiogroup"
        aria-label={t('projects.icon')}
        className="grid grid-cols-10 gap-1 rounded-lg border border-border p-1.5"
      >
        {PROJECT_ICONS.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={value === icon}
            aria-label={projectIcon(value)}
            onClick={() => onChange({ icon: value, color })}
            className={cn(
              'relative flex items-center justify-center rounded-md p-1 transition-colors',
              value === icon ? 'bg-sunken' : 'hover:bg-canvas',
            )}
          >
            <ProjectIcon icon={value} color={value === icon ? color : 'gray'} />
            {value === icon ? (
              <Check
                className="absolute -right-0.5 -top-0.5 size-3 rounded-full bg-surface text-accent-fg"
                aria-hidden="true"
              />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
};
